import { useNavigate } from 'react-router-dom';
import { MoreVertical, MessageCircle, Users, Clock } from 'lucide-react';
import { Agent } from '@/types';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn, formatDate, getStatusColor, truncate } from '@/lib/utils';
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface AgentCardProps {
  agent: Agent;
}

export function AgentCard({ agent }: AgentCardProps) {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    navigate(`/agent/${agent.agentId}`);
  };

  const statusConfig = {
    running: { label: '運行中', icon: '●', pulse: true },
    paused: { label: '暫停', icon: '●', pulse: false },
    draft: { label: '草稿', icon: '○', pulse: false },
    stopped: { label: '已停止', icon: '●', pulse: false },
  };

  const status = statusConfig[agent.status];

  return (
    <Card
      className={cn(
        "group relative overflow-hidden cursor-pointer card-hover animate-fade-in-up",
        isHovered && "border-primary/30"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      {/* Ambient glow effect */}
      <div className="absolute inset-0 bg-gradient-mesh opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      {/* Status indicator line */}
      <div
        className={cn(
          "absolute left-0 top-0 bottom-0 w-1 transition-all duration-300",
          getStatusColor(agent.status),
          agent.status === 'running' && status.pulse && "animate-pulse"
        )}
      />

      <div className="relative p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Status dot */}
            <div
              className={cn(
                "flex-shrink-0 w-2.5 h-2.5 rounded-full transition-all duration-300",
                getStatusColor(agent.status),
                agent.status === 'running' && status.pulse && "animate-pulse-soft shadow-lg"
              )}
            />

            {/* Name */}
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-semibold text-base text-foreground group-hover:text-primary transition-colors truncate">
                {agent.displayName}
              </h3>
              <p className="text-xs text-muted-foreground font-mono truncate">
                {agent.agentId}
              </p>
            </div>
          </div>

          {/* Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleClick(); }}>
                查看詳情
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => e.stopPropagation()}>
                編輯
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => e.stopPropagation()} className="text-destructive">
                刪除
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Description */}
        {agent.description && (
          <p className="text-sm text-muted-foreground mb-3 line-clamp-2 min-h-[2.5rem]">
            {truncate(agent.description, 80)}
          </p>
        )}

        {/* Tags */}
        {agent.tags && agent.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {agent.tags.slice(0, 3).map((tag) => (
              <Badge
                key={tag}
                variant="secondary"
                className="text-xs px-2 py-0.5"
              >
                {tag}
              </Badge>
            ))}
            {agent.tags.length > 3 && (
              <Badge variant="outline" className="text-xs px-2 py-0.5">
                +{agent.tags.length - 3}
              </Badge>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          {agent.stats && (
            <>
              <div className="flex items-center gap-1.5" title="今日訊息">
                <MessageCircle className="h-3.5 w-3.5" />
                <span className="font-medium">{agent.stats.todayMessages}</span>
              </div>
              <div className="flex items-center gap-1.5" title="活躍用戶">
                <Users className="h-3.5 w-3.5" />
                <span className="font-medium">{agent.stats.activeUsers}</span>
              </div>
            </>
          )}
          <div className="flex items-center gap-1.5" title="最後更新">
            <Clock className="h-3.5 w-3.5" />
            <span>{formatDate(agent.updatedAt)}</span>
          </div>
        </div>
      </div>
    </Card>
  );
}
